import React, { useState, useEffect } from 'react';
import axios from 'axios';

export default function TemplatesList() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { id, key, name, subject, content }
  const [creating, setCreating] = useState(false);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get('/speeddate/api/templates');
      setTemplates(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTemplates(); }, []);

  const handleSave = async (tpl) => {
    try {
      if (creating) {
        await axios.post('/speeddate/api/templates', tpl);
      } else {
        await axios.put(`/speeddate/api/templates/${tpl.key}`, tpl);
      }
      setEditing(null);
      setCreating(false);
      fetchTemplates();
    } catch (err) {
      window.Swal.fire('Errore', err.response?.data?.error || 'Errore salvataggio', 'error');
    }
  };

  const handleDelete = async (tpl) => {
    window.Swal.fire({
      title: 'Eliminare template?',
      text: `"${tpl.name}" verrà eliminato definitivamente.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Elimina',
      cancelButtonText: 'Annulla',
      confirmButtonColor: '#d33'
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          await axios.delete(`/speeddate/api/templates/${tpl.key}`);
          fetchTemplates();
          window.Swal.fire('Eliminato!', '', 'success');
        } catch (err) {
          window.Swal.fire('Errore', err.response?.data?.error || 'Errore', 'error');
        }
      }
    });
  };

  const openEditor = (tpl = null) => {
    if (tpl) {
      setEditing({ ...tpl });
      setCreating(false);
    } else {
      setEditing({ key: '', name: '', subject: '', content: '' });
      setCreating(true);
    }
  };

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>;

  return (
    <div className="wow fadeIn">
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-transparent border-0 pt-4 px-4 d-flex justify-content-between align-items-center">
          <h4 className="mb-0 fw-bold"><i className="fas fa-file-alt text-primary me-2"></i>Templates</h4>
          <button className="btn btn-primary btn-sm rounded-pill px-3 shadow-sm" onClick={() => openEditor()}>
            <i className="fas fa-plus me-1"></i> Nuovo Template
          </button>
        </div>
        <div className="card-body px-4">
          {templates.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <i className="fas fa-file-alt fa-2x mb-2 opacity-50"></i>
              <p className="mb-0">Nessun template. Creane uno nuovo.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th className="small text-uppercase fw-bold text-muted border-0">Nome</th>
                    <th className="small text-uppercase fw-bold text-muted border-0">Key</th>
                    <th className="small text-uppercase fw-bold text-muted border-0">Oggetto</th>
                    <th className="small text-uppercase fw-bold text-muted border-0">Anteprima</th>
                    <th className="small text-uppercase fw-bold text-muted border-0 text-end">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map(t => (
                    <tr key={t.id}>
                      <td className="fw-bold">{t.name}</td>
                      <td><code className="small text-muted">{t.key}</code></td>
                      <td className="small text-muted">{t.subject || '—'}</td>
                      <td>
                        <div
                          className="text-muted small"
                          style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          dangerouslySetInnerHTML={{ __html: (t.content || '').replace(/<[^>]*>/g, ' ').slice(0, 80) + '...' }}
                        />
                      </td>
                      <td className="text-end">
                        <button className="btn btn-sm btn-outline-primary rounded-pill me-2" onClick={() => openEditor(t)}>
                          <i className="fas fa-edit"></i>
                        </button>
                        <button className="btn btn-sm btn-outline-danger rounded-pill" onClick={() => handleDelete(t)}>
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Editor Modal */}
      {(editing) && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px' }}>
              <div className="modal-header border-0 pt-4 px-4">
                <h5 className="modal-title fw-bold">{creating ? 'Nuovo Template' : 'Modifica Template'}</h5>
                <button type="button" className="btn-close" onClick={() => { setEditing(null); setCreating(false); }}></button>
              </div>
              <div className="modal-body p-4">
                <div className="mb-3">
                  <label className="form-label small fw-bold text-muted">Nome</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="es. Come funziona"
                    value={editing.name}
                    onChange={e => setEditing({ ...editing, name: e.target.value })}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-bold text-muted">Key (identificativo univoco)</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="es. how-it-works"
                    value={editing.key}
                    disabled={!creating}
                    onChange={e => setEditing({ ...editing, key: e.target.value })}
                  />
                  <div className="text-muted small mt-1">Usa solo lettere minuscole, numeri e trattini. Non modificabile dopo la creazione.</div>
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-bold text-muted">Oggetto email</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="es. SOLTEROS: Ci vediamo stasera!"
                    value={editing.subject || ''}
                    onChange={e => setEditing({ ...editing, subject: e.target.value })}
                  />
                  <div className="text-muted small mt-1">Usato come oggetto email per template di tipo email. Lascia vuoto per template solo testo/HTML.</div>
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-bold text-muted">Contenuto HTML</label>
                  <textarea
                    className="form-control"
                    rows="8"
                    placeholder="<p>Inserisci il contenuto HTML...</p>"
                    value={editing.content}
                    onChange={e => setEditing({ ...editing, content: e.target.value })}
                    style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                  />
                </div>
              </div>
              <div className="modal-footer border-0 px-4 pb-4">
                <button className="btn btn-secondary rounded-pill px-4" onClick={() => { setEditing(null); setCreating(false); }}>
                  Annulla
                </button>
                <button className="btn btn-primary rounded-pill px-4 shadow-sm" onClick={() => handleSave(editing)}>
                  {creating ? 'Crea Template' : 'Salva Modifiche'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}